import { ClockIcon } from "@heroicons/react/24/outline";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useDelayRecipeEditor } from "../delay-recipe-editor";
import { EditableWorkflow } from "../types";
import { StepTimingMode, stepTimingMode } from "./builder-helper";

const DEFAULT_WAIT_DELAY = 10;
const DEFAULT_WAIT_UNIT = "m";

interface StepTimingEditorProps {
    workflow: EditableWorkflow;
    verb: string;
    onCommit: (delayRecipe: string) => void;
}

export const StepTimingEditor: React.FC<StepTimingEditorProps> = props => {
    const { workflow, verb, onCommit } = props;
    const control = useDelayRecipeEditor(workflow, onCommit);
    const mode = stepTimingMode(control.delay);

    const handleModeChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newMode = e.target.value as StepTimingMode;
            if (newMode === "immediately") {
                control.commitDelay(0, "m");
            } else {
                control.commitDelay(DEFAULT_WAIT_DELAY, DEFAULT_WAIT_UNIT);
            }
        },
        [control],
    );

    if (!control.visible) {
        return null;
    }

    return (
        <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
            <ClockIcon className="w-4 h-4 text-gray-400 shrink-0" />
            <span>{verb}</span>
            <Select
                disabled={!control.editable}
                textSize="sm"
                className="w-36"
                value={mode}
                onChange={handleModeChange}>
                <option value="immediately">immediately</option>
                <option value="wait">after a wait</option>
            </Select>
            {mode === "immediately" ? (
                <span>when the trigger fires</span>
            ) : (
                <>
                    <span>of</span>
                    <div className="w-20">
                        <Input
                            disabled={!control.editable}
                            textSize="sm"
                            type="number"
                            min={1}
                            value={control.delay}
                            onChange={control.handleDelayChange}
                            onBlur={control.commitDelayChange}
                        />
                    </div>
                    <Select
                        disabled={!control.editable}
                        textSize="sm"
                        className="w-28"
                        value={control.unit}
                        onChange={control.handleUnitsChange}>
                        <option value="m">minutes</option>
                        <option value="h">hours</option>
                        <option value="d">days</option>
                    </Select>
                    <span>from the trigger</span>
                </>
            )}
            {control.pending ? <Spinner /> : null}
        </div>
    );
};
